/** @jsxImportSource @emotion/react */
import { Interpolation, Theme } from "@emotion/react";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import Colors, { shadeColor } from "styles/colors";
import Chip from "components/atoms/Chip.atom";
import AddTagButton from "pages/reports/bi-dashboard-next/components/molecules/Reports.AddTagButton.molecule";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarLight } from "@fortawesome/pro-light-svg-icons";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { faCalendarDays, faEnvelope } from "@fortawesome/pro-duotone-svg-icons";
import { useReportTags } from "pages/reports/bi-dashboard-next/hooks/useReportTags";
import { ReportTagDTO } from "pages/reports/bi-dashboard-next/models/ReportTagDTO";
import { useTranslation } from "react-i18next";
import { DialogModal } from "components/molecules/DialogModal.molecule";
import { EmailScheduleModal } from "pages/reports/bi-dashboard-next/components/modals/EmailSchedule.modal";
import { SelectScheduleModal } from "pages/reports/bi-dashboard-next/components/modals/SelectSchedule.modal";
import { EmailReportModal } from "pages/reports/bi-dashboard-next/components/modals/EmailReport.modal";
import { ReportDTO } from "pages/reports/bi-dashboard-next/models/ReportDTO";
import { EmailScheduleDTO } from "pages/reports/bi-dashboard-next/hooks/useEmailSchedule";
import { useReportScheduleModals } from "pages/reports/bi-dashboard-next/hooks/useReportScheduleModals";

interface ReportsListViewRowProps {
  title: string;
  description: string;
  tags?: ReportTagDTO[];
  reportId: number;
  reportNumber: string;
  css?: Interpolation<Theme>;
  onTagClick?: (tagName: string) => void;
  className?: string;
  favorited?: boolean;
  onFavoriteToggle?: (reportId: number, favorited: boolean) => void;
  isTogglingFavorite?: boolean;
  emailScheduleCount?: number;
}

export const ReportsListViewRow: React.FC<ReportsListViewRowProps> = ({
  title,
  description,
  tags,
  reportId,
  reportNumber,
  css: cssProp,
  onTagClick,
  className,
  favorited = false,
  onFavoriteToggle,
  isTogglingFavorite = false,
  emailScheduleCount = 0,
}) => {
  const { t } = useTranslation("reports");
  const dispatch = useDispatch();
  const { deleteTag, isDeletingTag, canManageTags } = useReportTags();

  const [deletingTagId, setDeletingTagId] = useState<number | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<number | null>(null);

  const {
    showSelectScheduleModal,
    setShowSelectScheduleModal,
    showEmailScheduleModal,
    setShowEmailScheduleModal,
    showEmailReportModal,
    setShowEmailReportModal,
    schedules,
    selectedSchedule,
    isLoading,
    reportObject,
    handleShowScheduleModal,
    handleShowCreateScheduleModal,
    handleShowUpdateScheduleModal,
  } = useReportScheduleModals({
    reportId,
    title,
    description,
    reportNumber,
    emailScheduleCount,
  });

  const handleDeleteTag = (tagId: number) => {
    setTagToDelete(tagId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteTag = () => {
    if (tagToDelete !== null) {
      setDeletingTagId(tagToDelete);
      deleteTag({ reportId, tagId: tagToDelete });
      setShowDeleteConfirmation(false);
    }
  };

  const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    dispatch({
      type: "REPORT_DETAIL",
      payload: { reportId: reportNumber },
    });
  };

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isTogglingFavorite) {
      return;
    }

    if (onFavoriteToggle) {
      onFavoriteToggle(reportId, !favorited);
    }
  };

  return (
    <>
      <div
        data-qa="report-list-view-row"
        className={`report-list-view-row ${className || ""}`}
        onClick={handleRowClick}
        css={[
          {
            display: "flex",
            justifyContent: "space-between",
            padding: 10,
            cursor: "pointer",
            "&:hover": {
              background: Colors.biDashboardView.ROW_HOVER_BACKGROUND,
            },
          },
          cssProp,
        ]}
      >
        <div css={{ display: "flex", flexDirection: "column" }}>
          <div css={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
          <div>{description}</div>
          {tags ? (
            <div
              css={{
                display: "flex",
                flexWrap: "wrap",
                gap: 5,
                marginTop: 5,
              }}
            >
              {tags.map((tag) => (
                <Chip
                  key={`ReportListView-TagChip-${tag.id}`}
                  data-qa={`tag-chip-${tag.id}`}
                  label={
                    tag.tagName.length > 24
                      ? tag.tagName.slice(0, 23) + "..."
                      : tag.tagName
                  }
                  disabled={deletingTagId === tag.id && isDeletingTag}
                  endIcon={
                    deletingTagId === tag.id && isDeletingTag ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        css={{ animation: "spin 1s linear infinite" }}
                      />
                    ) : undefined
                  }
                  css={{
                    background: Colors.biDashboardView.TAG_BACKGROUND,
                    color: Colors.biDashboardView.TAG_TEXT_COLOR,
                    ":hover": {
                      background: shadeColor(
                        Colors.biDashboardView.TAG_BACKGROUND,
                        -0.05,
                      ),
                    },
                    'span[role="button"], svg[role="button"]': {
                      background: Colors.biDashboardView.TAG_BUTTON_BACKGROUND,
                      color: Colors.biDashboardView.TAG_DELETE_ICON_BACKGROUND,
                      ":hover": {
                        color: shadeColor(
                          Colors.biDashboardView.TAG_DELETE_ICON_BACKGROUND,
                          -0.05,
                        ),
                      },
                    },
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                  onDelete={
                    !canManageTags ||
                    (deletingTagId === tag.id && isDeletingTag)
                      ? undefined
                      : () => handleDeleteTag(tag.id)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onTagClick) {
                      onTagClick(tag.tagName);
                    }
                  }}
                />
              ))}
              {canManageTags && <AddTagButton reportId={reportId} />}
            </div>
          ) : null}
        </div>
        <div css={{ flex: 1 }} />
        <div
          css={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            color: Colors.biDashboardView.TAG_DELETE_ICON_BACKGROUND,
            background: "transparent",
            fontSize: 20,
          }}
        >
          {isTogglingFavorite ? (
            <div css={{ padding: "8px 0" }}>
              <FontAwesomeIcon
                icon={faSpinner}
                css={{
                  animation: "spin 1s linear infinite",
                }}
                data-qa="favorite-spinner"
              />
            </div>
          ) : favorited ? (
            <div
              css={{
                padding: "8px 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFavoriteToggle(e);
              }}
              data-qa="star-icon-container"
            >
              <FontAwesomeIcon
                icon={faStar}
                css={{
                  color: Colors.biDashboardView.FAVORITE_STAR,
                }}
                data-qa="star-icon"
              />
            </div>
          ) : (
            <div
              css={{
                padding: "8px 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFavoriteToggle(e);
              }}
              data-qa="star-light-icon-container"
            >
              <FontAwesomeIcon icon={faStarLight} data-qa="star-light-icon" />
            </div>
          )}
          <div
            css={{
              padding: "8px 0",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowEmailReportModal(true);
            }}
            data-qa="email-icon-container"
          >
            <FontAwesomeIcon icon={faEnvelope} data-qa="email-icon" />
          </div>
          <div
            css={{
              padding: "8px 0",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleShowScheduleModal();
            }}
            data-qa="calendar-icon-container"
          >
            <FontAwesomeIcon icon={faCalendarDays} data-qa="calendar-icon" />
            {emailScheduleCount > 0 && (
              <div
                css={{
                  position: "absolute",
                  top: 0,
                  left: 8,
                  backgroundColor: Colors.highlight.BRIGHT_BLUE,
                  color: "white",
                  borderRadius: "50%",
                  minWidth: "14px",
                  height: "14px",
                  fontSize: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  padding: "0 4px",
                }}
                data-qa="email-schedule-badge"
              >
                {emailScheduleCount}
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogModal
        show={showDeleteConfirmation}
        onHide={() => setShowDeleteConfirmation(false)}
        title={null}
        cancelButtonText={t("Cancel")}
        submitButtonText={t("Delete")}
        submitButtonVariant="danger"
        onSubmit={confirmDeleteTag}
        data-qa="delete-tag-confirmation-dialog"
      >
        <div css={{ padding: "10px 0" }}>
          {t("Are you sure you want to delete this tag?")}
        </div>
      </DialogModal>

      <SelectScheduleModal
        show={showSelectScheduleModal}
        hide={() => setShowSelectScheduleModal(false)}
        report={reportObject}
        schedules={schedules}
        isLoading={isLoading}
        showCreateScheduleModal={handleShowCreateScheduleModal}
        showUpdateScheduleModal={handleShowUpdateScheduleModal}
      />

      <EmailScheduleModal
        show={showEmailScheduleModal}
        hide={() => setShowEmailScheduleModal(false)}
        report={reportObject}
        schedule={selectedSchedule as EmailScheduleDTO | undefined}
      />

      <EmailReportModal
        show={showEmailReportModal}
        hide={() => setShowEmailReportModal(false)}
        report={reportObject}
      />
    </>
  );
};
